import React, { useState, useEffect } from 'react';
import { Upload, Trash2, Edit2, Image as ImageIcon, Youtube, FileText } from 'lucide-react';
import AdminNavbar from '../components/admin/AdminNavbar';
import { GalleryImage, uploadGalleryImage, fetchGalleryImages, updateGalleryImage, deleteGalleryImage, getGalleryImageUrl, getGalleryThumbnailUrl } from '../services/galleryService';
import { getAvailableCountries } from '../data/countryCoordinates';
import { MAX_GALLERY_IMAGE_BYTES, isFileSizeError } from '../utils/validation';

interface GalleryFormData {
  image: File | null;
  country: string;
  year: string;
  youtubeUrl: string;
  description: string;
}

const AdminGalleryManagementPage: React.FC = () => {
  const [formData, setFormData] = useState<GalleryFormData>({
    image: null,
    country: '',
    year: new Date().getFullYear().toString(),
    youtubeUrl: '',
    description: '',
  });

  const [images, setImages] = useState<GalleryImage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<string | null>(null);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);

  // Fetch all gallery images on component mount
  useEffect(() => {
    fetchAllImages();
  }, []);

  const fetchAllImages = async () => {
    setIsLoading(true);
    try {
      const data = await fetchGalleryImages();
      // Sort by uploadedAt descending (most recent first)
      const sorted = data.sort((a, b) => {
        const timeA = a.uploadedAt?._seconds || 0;
        const timeB = b.uploadedAt?._seconds || 0;
        return timeB - timeA;
      });
      setImages(sorted);
    } catch (error) {
      console.error('Error fetching gallery images:', error);
      setSubmitStatus('Error loading gallery images. Please refresh the page.');
    } finally {
      setIsLoading(false);
    }
  };

  const clearSelectedImage = () => {
    setFormData(prev => ({ ...prev, image: null }));
    setImagePreview(null);
    setImageError(null);
    setSubmitStatus(current => (current && isFileSizeError(current) ? null : current));
    const fileInput = document.getElementById('image-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFormData({ ...formData, image: file });

    if (!file.type.startsWith('image/')) {
      setImageError('Gallery image must be an image file.');
      setImagePreview(null);
      return;
    }

    if (file.size > MAX_GALLERY_IMAGE_BYTES) {
      const message = 'Image must be 10MB or smaller. Remove this image to keep the rest of the form.';
      setImageError(message);
      setSubmitStatus(message);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      return;
    }

    setImageError(null);
    setSubmitStatus(current => (current && isFileSizeError(current) ? null : current));
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isUpdateMode) {
      await handleUpdate();
    } else {
      await handleCreate();
    }
  };

  const handleCreate = async () => {
    // Validate form
    if (!formData.country.trim()) {
      setSubmitStatus('Please enter a country name.');
      return;
    }
    if (!formData.year.match(/^\d{4}$/)) {
      setSubmitStatus('Please enter a valid 4-digit year.');
      return;
    }

    if (formData.image && formData.image.size > MAX_GALLERY_IMAGE_BYTES) {
      const message = 'Image must be 10MB or smaller. Remove this image to keep the rest of the form.';
      setImageError(message);
      setSubmitStatus(message);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formDataToSend = new FormData();
      if (formData.image) {
        formDataToSend.append('image', formData.image);
      }
      formDataToSend.append('country', formData.country.trim());
      formDataToSend.append('year', formData.year);
      if (formData.youtubeUrl.trim()) {
        formDataToSend.append('youtubeUrl', formData.youtubeUrl.trim());
      }
      if (formData.description.trim()) {
        formDataToSend.append('description', formData.description.trim());
      }

      await uploadGalleryImage(formDataToSend);
      
      setSubmitStatus('✓ Gallery item uploaded successfully!');
      
      // Reset form
      setFormData({
        image: null,
        country: '',
        year: new Date().getFullYear().toString(),
        youtubeUrl: '',
        description: '',
      });
      setImagePreview(null);
      setImageError(null);
      
      // Refresh gallery list
      await fetchAllImages();
      
      // Clear file input
      const fileInput = document.getElementById('image-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      
    } catch (error: any) {
      console.error('Error uploading image:', error);
      const message = error.message || 'Failed to upload image. Please try again.';
      if (isFileSizeError(message) || /File too large/i.test(message)) {
        setImageError('Image must be 10MB or smaller.');
        setSubmitStatus(`${message} Remove the image to keep the rest of the form.`);
      } else {
        setSubmitStatus(`Error: ${message}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedImage) return;

    // Validate form
    if (!formData.country.trim()) {
      setSubmitStatus('Please enter a country name.');
      return;
    }
    if (!formData.year.match(/^\d{4}$/)) {
      setSubmitStatus('Please enter a valid 4-digit year.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const updates: Partial<Pick<GalleryImage, 'country' | 'year' | 'youtubeUrl' | 'description'>> = {
        country: formData.country.trim(),
        year: formData.year,
        youtubeUrl: formData.youtubeUrl.trim(),
        description: formData.description.trim(),
      };

      await updateGalleryImage(selectedImage.id, updates);
      
      setSubmitStatus('✓ Gallery item updated successfully!');
      
      // Reset form
      handleCancelUpdate();
      
      // Refresh gallery list
      await fetchAllImages();
      
    } catch (error: any) {
      console.error('Error updating image:', error);
      setSubmitStatus(`Error: ${error.message || 'Failed to update image. Please try again.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditImage = (image: GalleryImage) => {
    setSelectedImage(image);
    setIsUpdateMode(true);
    setFormData({
      image: null, // Can't update the actual image file
      country: image.country,
      year: image.year,
      youtubeUrl: image.youtubeUrl || '',
      description: image.description || '',
    });
    setImagePreview(getGalleryImageUrl(image.filename));
    setImageError(null);
    setSubmitStatus(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelUpdate = () => {
    setIsUpdateMode(false);
    setSelectedImage(null);
    setFormData({
      image: null,
      country: '',
      year: new Date().getFullYear().toString(),
      youtubeUrl: '',
      description: '',
    });
    setImagePreview(null);
    setImageError(null);
    setSubmitStatus(null);
    
    // Clear file input
    const fileInput = document.getElementById('image-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const handleDeleteImage = async (image: GalleryImage) => {
    if (!window.confirm(`Are you sure you want to delete the gallery item from ${image.country} (${image.year})? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteGalleryImage(image.id);
      setSubmitStatus('✓ Gallery item deleted successfully!');
      await fetchAllImages();
    } catch (error: any) {
      console.error('Error deleting image:', error);
      setSubmitStatus(`Error: ${error.message || 'Failed to delete image. Please try again.'}`);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--secondary-pearl)]">
      <AdminNavbar />
      
      <div className="py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <h1 className="heading-primary text-[var(--primary-deep-blue)] mb-2">
                Gallery Management
              </h1>
              <p className="body-regular text-[var(--secondary-slate)]">
                Upload and manage gallery items with images and YouTube videos
              </p>
            </div>

            {/* Form Section */}
            <div className="card mb-8">
              <div className="card-content">
                <h2 className="heading-tertiary text-[var(--primary-deep-blue)] border-b border-[var(--neutral-medium-gray)] pb-2 mb-6">
                  {isUpdateMode ? 'Update Gallery Item' : 'Upload New Gallery Item'}
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Country and Year */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-[var(--neutral-charcoal)] mb-2">
                        Country *
                      </label>
                      <select
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 text-sm border border-[var(--neutral-medium-gray)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary-deep-blue)]"
                        disabled={isSubmitting}
                        required
                      >
                        <option value="">Select a country</option>
                        {getAvailableCountries().sort().map((country) => (
                          <option key={country} value={country}>
                            {country}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-[var(--neutral-charcoal)] mb-2">
                        Year *
                      </label>
                      <input
                        type="text"
                        name="year"
                        value={formData.year}
                        onChange={handleInputChange}
                        placeholder="e.g., 2024"
                        pattern="\d{4}"
                        className="w-full px-4 py-2 text-sm border border-[var(--neutral-medium-gray)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary-deep-blue)]"
                        disabled={isSubmitting}
                        required
                      />
                    </div>
                  </div>

                  {/* Image Upload */}
                  <div>
                    <label className="block text-sm font-semibold text-[var(--neutral-charcoal)] mb-2">
                      {isUpdateMode ? 'Current Image (Cannot be changed)' : 'Image (Optional)'}
                    </label>
                    {imagePreview && (
                      <div className="mb-4">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="w-full max-w-md h-64 object-cover rounded-lg border-2 border-[var(--neutral-medium-gray)]"
                        />
                      </div>
                    )}
                    {!isUpdateMode && (
                      <div className="relative space-y-2">
                        <input
                          type="file"
                          id="image-upload"
                          accept="image/*"
                          onChange={handleFileChange}
                          className="hidden"
                          disabled={isSubmitting}
                        />
                        <label
                          htmlFor="image-upload"
                          className={`flex items-center justify-center w-full px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer hover:bg-[var(--secondary-pearl)] transition-colors ${
                            imageError ? 'border-[var(--error)]' : 'border-[var(--primary-deep-blue)]'
                          }`}
                        >
                          <Upload className="w-5 h-5 mr-2 text-[var(--primary-deep-blue)]" />
                          <span className="text-sm text-[var(--primary-deep-blue)]">
                            {formData.image ? formData.image.name : 'Click to select image'}
                          </span>
                        </label>
                        <p className="text-xs text-[var(--secondary-slate)]">
                          JPG, PNG, or similar — max 10MB
                        </p>
                        {formData.image && (
                          <button
                            type="button"
                            onClick={clearSelectedImage}
                            className="text-xs font-medium text-[var(--error)] hover:underline"
                          >
                            Remove image
                          </button>
                        )}
                        {imageError && (
                          <p className="text-xs text-red-500">{imageError}</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* YouTube URL */}
                  <div>
                    <label className="block text-sm font-semibold text-[var(--neutral-charcoal)] mb-2">
                      <Youtube className="inline w-4 h-4 mr-1" />
                      YouTube URL or Embed Code (Optional)
                    </label>
                    <textarea
                      name="youtubeUrl"
                      value={formData.youtubeUrl}
                      onChange={handleInputChange}
                      placeholder="Paste YouTube URL or full iframe embed code here..."
                      rows={3}
                      className="w-full px-4 py-2 text-sm border border-[var(--neutral-medium-gray)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary-deep-blue)] font-mono"
                      disabled={isSubmitting}
                    />
                    <p className="text-xs text-[var(--secondary-slate)] mt-1">
                      Accepts: URLs (youtube.com/watch?v=...), short URLs (youtu.be/...), or full iframe embed code
                    </p>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-semibold text-[var(--neutral-charcoal)] mb-2">
                      <FileText className="inline w-4 h-4 mr-1" />
                      Description (Optional)
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Add a description for this image..."
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-2 text-sm border border-[var(--neutral-medium-gray)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary-deep-blue)]"
                      disabled={isSubmitting}
                    />
                    <p className="text-xs text-[var(--secondary-slate)] mt-1">
                      {formData.description.length}/500 characters
                    </p>
                  </div>

                  {/* Status Message */}
                  {submitStatus && (
                    <div className={`p-3 rounded-lg text-sm ${
                      submitStatus.startsWith('✓') 
                        ? 'bg-green-100 text-green-800 border border-green-300' 
                        : 'bg-red-100 text-red-800 border border-red-300'
                    }`}>
                      <p className="font-semibold">{submitStatus}</p>
                      {!submitStatus.startsWith('✓') && formData.image && (isFileSizeError(submitStatus) || imageError) && (
                        <button
                          type="button"
                          onClick={clearSelectedImage}
                          className="mt-2 px-3 py-1.5 text-xs font-medium bg-white text-red-800 border border-red-300 rounded-md hover:bg-red-50"
                        >
                          Remove image
                        </button>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-4">
                    <button
                      type="submit"
                      disabled={isSubmitting || !formData.country || !formData.year}
                      className="flex-1 px-6 py-2 text-sm gradient-primary text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {isSubmitting ? 'Processing...' : isUpdateMode ? 'Update Item' : 'Upload Item'}
                    </button>
                    
                    {isUpdateMode && (
                      <button
                        type="button"
                        onClick={handleCancelUpdate}
                        disabled={isSubmitting}
                        className="px-6 py-2 text-sm bg-[var(--neutral-medium-gray)] text-[var(--neutral-charcoal)] rounded-lg font-semibold hover:bg-[var(--neutral-dark-gray)] transition-all"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {/* Gallery List */}
            <div className="card">
              <div className="card-content">
                <h2 className="heading-tertiary text-[var(--primary-deep-blue)] border-b border-[var(--neutral-medium-gray)] pb-2 mb-6">
                  Existing Gallery Items ({images.length})
                </h2>

                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--primary-deep-blue)]"></div>
                  </div>
                ) : images.length === 0 ? (
                  <div className="text-center py-12">
                    <ImageIcon className="w-16 h-16 mx-auto mb-4 text-[var(--neutral-medium-gray)]" />
                    <p className="text-sm text-[var(--secondary-slate)]">
                      No gallery items yet. Upload your first item above!
                    </p>
                  </div>
                ) : (
                  <div className="space-y-8">
                    {/* Group images by year */}
                    {Object.entries(
                      images.reduce((acc, image) => {
                        const year = image.year;
                        if (!acc[year]) {
                          acc[year] = [];
                        }
                        acc[year].push(image);
                        return acc;
                      }, {} as Record<string, GalleryImage[]>)
                    )
                    .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA)) // Sort years descending
                    .map(([year, yearImages]) => (
                      <div key={year}>
                        {/* Year Header */}
                        <div className="flex items-center mb-4">
                          <h3 className="text-xl font-bold text-[var(--primary-deep-blue)] mr-3">
                            {year}
                          </h3>
                          <div className="flex-1 h-px bg-[var(--neutral-medium-gray)]"></div>
                          <span className="ml-3 text-sm text-[var(--secondary-slate)] font-medium">
                            {yearImages.length} {yearImages.length === 1 ? 'item' : 'items'}
                          </span>
                        </div>

                        {/* Images Grid for this year */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {yearImages.map((image) => (
                            <div
                              key={image.id}
                              className="bg-[var(--secondary-pearl)] rounded-lg overflow-hidden border border-[var(--neutral-medium-gray)] hover:shadow-lg transition-shadow"
                            >
                              {/* Image */}
                              <div className="aspect-video bg-gray-200 relative">
                                {image.filename ? (
                                  <img
                                    src={getGalleryThumbnailUrl(image)}
                                    alt={`${image.country} ${image.year}`}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center gradient-neutral-br">
                                    <div className="text-center">
                                      <ImageIcon className="w-12 h-12 mx-auto mb-2 text-[var(--secondary-slate)]" />
                                      <p className="text-xs text-[var(--secondary-slate)]">No Image</p>
                                    </div>
                                  </div>
                                )}
                                {image.youtubeUrl && (
                                  <div className="absolute top-2 right-2 bg-red-600 text-white px-2 py-1 rounded text-xs font-semibold flex items-center">
                                    <Youtube className="w-3 h-3 mr-1" />
                                    VIDEO
                                  </div>
                                )}
                              </div>

                              {/* Info */}
                              <div className="p-4">
                                <h3 className="font-bold text-base text-[var(--neutral-charcoal)] mb-1">
                                  {image.country}
                                </h3>
                                <p className="text-xs text-[var(--secondary-slate)] mb-2">
                                  Year: {image.year}
                                </p>
                                {image.description && (
                                  <p className="text-xs text-[var(--secondary-slate)] mb-3 line-clamp-2">
                                    {image.description}
                                  </p>
                                )}

                                {/* Actions */}
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleEditImage(image)}
                                    className="flex-1 btn-action-primary text-xs !px-3 !py-2 !rounded"
                                  >
                                    <Edit2 className="w-3 h-3 mr-1" />
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteImage(image)}
                                    className="btn-action-red text-xs flex items-center justify-center"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminGalleryManagementPage;

